/* eslint-disable @typescript-eslint/no-explicit-any */

// Imports for external libraries go here.
import React, { FC, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { EditableComponent } from '@adobe/aem-react-editable-components';

// Imports for internal (to the monorepo) libraries go here,
import { LuxurySuitesProps } from './LuxurySuites.types';
import { StyledLuxurySuites } from './LuxurySuites.styles';
import { SuitesFilterPanel } from '../../molecules/SuitesFilterPanel';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { Pill, Pagination, PillCollection, renditions } from '@marriott/mi-ui-library';
import { SuitesCard } from '../../molecules/SuitesCard';
import {
  FILTER_PARAMS,
  FILTER_TYPES,
  MOBILE_COL_LENGTH,
  MOBILE_NAVBAR_HEIGHT,
  NAVBAR_HEIGHT,
  parameterMap,
} from '../../modules/constants/constant';
import {
  constructPayloadFromUrl,
  FiltersPillSelection,
  getCurrentSuites,
  getDirection,
  getSubstringAfterFirst,
  getUpdatedFiltersData,
  shuffleCards,
  totalFilterCount,
  updateURLParams,
  useViewport,
} from '../../modules/helpers';
import { useSession } from '@marriott/mi-headless-utils';
import { FEATURE_FLAGS } from '@marriott/mi-feature-flag-utils';

// Use named rather than default exports.
export const LuxurySuites: FC<LuxurySuitesProps> = props => {
  const clientEnvVars = useClientEnvVarsStore.getState().envVarsObject;
  const { ACCEPT_LANGUAGE: currentLocale } = clientEnvVars;
  const isLogoFlagEnabled = props?.featureFlagLogo?.[FEATURE_FLAGS.EXPERIENCE_MCOM_LOGO_AUTOMATION];

  //state's Heree....
  const [data, setData] = useState<any>([]);
  const [suitesData, setSuitesData] = useState<any>([]);
  const [filtersData, setFiltersData] = useState<any>([]);
  const [popupOpenState, setPopupOpenState] = useState<boolean>(false);

  const [filtersCount, setFiltersCount] = useState<number>(0);
  const [filtersPayload, setFiltersPayload] = useState<Array<{ type: string; dimensions: Array<string> }>>([]);

  //pagination
  const [currentPage, setCurrentPage] = useState<number>(1);
  const { session }: any = useSession();

  //direction
  const [direction, setDirection] = useState(false);
  useEffect(() => {
    setDirection(getDirection());
  }, []);

  // State for pills data - initially empty, will be populated by useEffect
  const [pillsData, setPillsData] = useState<any[]>([]);

  const [filterButtonClicked, setFilterButtonClicked] = useState<any>({
    AllFilters: false,
    Brands: false,
    Location: false,
    Bedrooms: false,
  });

  /**
   * Default Filter's state ....
   */
  const updateFiltersPayload = () => {
    const filtersPayload = [
      {
        type: FILTER_TYPES.BRANDS,
        dimensions: [],
        codes: [],
      },
      {
        type: FILTER_TYPES.COUNTRY,
        dimensions: [],
        codes: [],
      },
      {
        type: FILTER_TYPES.BEDROOMS,
        dimensions: [FILTER_TYPES.ANY_BEDROOMS],
        codes: [],
      },
      {
        type: FILTER_TYPES.CONNECTING_ROOMS,
        dimensions: [],
        codes: [],
      },
    ];
    return filtersPayload;
  };

  const { updateSession } = useSession();

  const fetchSuitesData = async () => {
    try {
      const response = await fetch(`${props?.suitesEndpoint}`, {
        method: 'GET',
        headers: {
          'accept-language': currentLocale,
        },
      });
      const data = await response?.json();
      /**
       * Getting the query params from URL too perform deeplinking....
       */

      const url = window.location.search;

      const initialFilters = updateFiltersPayload(); // if async, add await

      const filtersInSession: any = session?.data?.LuxurySuites?.filters ?? initialFilters;

      const filtersPayloadFromParams = constructPayloadFromUrl(url, initialFilters, parameterMap);

      // calculating the filters count from queryparams
      const filtersParamCount = totalFilterCount(filtersPayloadFromParams);

      // Calculate total filter count
      const filtersCount = filtersParamCount || (session?.data?.LuxurySuites?.filtersCount ?? 0);

      /**
       * prioritizing the query params info instead of the session data
       */
      const payload = filtersParamCount || filtersCount === 0 ? filtersPayloadFromParams : filtersInSession;

      setFiltersPayload(payload);
      setFiltersCount(filtersCount);

      if (data && data?.suites?.length !== 0 && data?.filters?.length !== 0 && !filtersCount) {
        const suites = props?.isRandom ? shuffleCards(data?.suites) : data?.suites;
        setSuitesData(suites);
        setFiltersData(data?.filters);
        setData(data);
      } else {
        const currentSuites = getCurrentSuites(payload, data?.suites);
        const shuffledSuites =
          (currentSuites?.length !== 0 && (props?.isRandom ? shuffleCards(currentSuites) : currentSuites)) || [];

        setSuitesData(shuffledSuites);

        /**
         *
         * @param payload | filter's payload
         * @returns | return's the updated available filter's data...
         */
        const updatedFiltersRes = (payload: any) => {
          // Get updated filters
          const activeFilters = payload?.filter(
            (f: { dimensions: string[] }) => f?.dimensions && f?.dimensions?.length > 0
          );
          const updatedFilters =
            activeFilters?.length !== 0 ? getUpdatedFiltersData(data?.filters, payload, data?.suites) : data?.filters;
          return updatedFilters;
        };

        /**
         * give's the updated filter's response...
         */
        const updatedFilters = updatedFiltersRes(payload);

        setFiltersData(updatedFilters);
        setData(data);
      }
    } catch (error) {
      console.log('***err***', error);
    }
  };

  useEffect(() => {
    data?.length === 0 && session !== undefined && fetchSuitesData();
  }, [session]);

  // useEffect to populate pills data from filters prop
  useEffect(() => {
    /***
     * Check if Bedrooms is selected in modal...
     * For BedRooms, ignore if "any" is present in dimensions
     */
    const isBedroomsSelected = FiltersPillSelection(filtersPayload, FILTER_TYPES.BEDROOMS);
    const isLocationSelected = FiltersPillSelection(filtersPayload, FILTER_TYPES.COUNTRY);
    const isBrandsSelected = FiltersPillSelection(filtersPayload, FILTER_TYPES.BRANDS);

    // Always start with the AllFilters pill
    const allFiltersPill = {
      label: props?.allFiltersLabel,
      value: 'AllFilters',
      isActive: filtersCount !== undefined && filtersCount > 0,
      showIcon: filtersCount === 0,
      iconClass: 'icon-dining-filter',
      count: filtersCount,
      custom_click_track_value: props?.allFiltersClickTrackValue,
      buttonClassName: 'd-flex align-items-center all-filters-button',
      id: 'all filters',
      className: 'custom_click_track',
    };

    if (props?.filters && props?.filters.length > 0) {
      // Create pills from the filters list...
      const newPillsData = props?.filters.map(
        (pill: { filterTitle: string; filterId: string; clickTrackValue: string }) => ({
          label: pill.filterTitle,
          value:
            (pill.filterId === FILTER_TYPES.BRANDS && 'Brands') ||
            (pill.filterId === FILTER_TYPES.BEDROOMS && 'Bedrooms') ||
            (pill.filterId === FILTER_TYPES.COUNTRY && 'Location'),
          isActive:
            (pill.filterId === FILTER_TYPES.BRANDS && isBrandsSelected) ||
            (pill.filterId === FILTER_TYPES.BEDROOMS && isBedroomsSelected) ||
            (pill.filterId === FILTER_TYPES.COUNTRY && isLocationSelected),
          custom_click_track_value: pill?.clickTrackValue,
          id: pill.filterId,
          className: 'custom_click_track',
        })
      );

      // Set pills data with AllFilters first, then the rest from filters prop
      setPillsData([allFiltersPill, ...newPillsData]);
    } else {
      // If no filters prop, just show AllFilters
      setPillsData([allFiltersPill]);
    }
  }, [filtersCount]);

  // Handle pill click to toggle selection
  const handlePillClick = (filterId: string) => {
    const checkIfPresent = filterId in filterButtonClicked;
    Object.keys(filterButtonClicked)?.forEach((item: string) => {
      if (item === filterId) {
        filterButtonClicked[item] = checkIfPresent;
      }
    });

    setFilterButtonClicked(filterButtonClicked);
    setPopupOpenState(true);
  };

  const updateSessionObj = async (filtersData: { type: string; dimensions: Array<string> }[], filterCnt: number) => {
    const sessionObj: any = {
      data: {
        LuxurySuites: {
          filters: filtersData,
          filtersCount: filterCnt,
        },
      },
    };
    await updateSession(sessionObj);
  };

  const onChange = (filterId: string, filterTag: string) => {
    let payload = filtersPayload;
    const targetFilter = props?.quickFilters?.find((el: { filterId: string }) => el?.filterId === filterId);

    if (targetFilter) {
      payload = filtersPayload?.map(obj => {
        if (obj?.type === filterId) {
          const existingDimensions = obj?.dimensions || [];
          const isTagPresent = existingDimensions?.includes(filterTag);

          // Special handling for bedrooms
          if (filterId === FILTER_TYPES.BEDROOMS) {
            const anyTag = FILTER_TYPES.ANY_BEDROOMS;
            const hasAny = existingDimensions?.includes(anyTag);

            if (isTagPresent) {
              // Remove the selected tag
              const updatedDimensions = existingDimensions?.filter((tag: string) => tag !== filterTag);

              // If no other bedroom filters remain (except 'any'), add 'any' back
              const remainingBedroomFilters = updatedDimensions?.filter(
                (tag: string) => tag !== anyTag && tag?.startsWith('mi-luxury-spa:bedrooms/')
              );

              return {
                ...obj,
                dimensions: remainingBedroomFilters?.length === 0 ? [...updatedDimensions, anyTag] : updatedDimensions,
              };
            } else {
              // Add the new tag
              let updatedDimensions = [...existingDimensions, filterTag];

              // Remove 'any' if we're adding a specific bedroom filter
              if (hasAny && filterTag !== anyTag) {
                updatedDimensions = updatedDimensions?.filter(tag => tag !== anyTag);
              }

              return {
                ...obj,
                dimensions: updatedDimensions,
              };
            }
          } else {
            if (obj?.type === FILTER_TYPES.CONNECTING_ROOMS) {
              const tag = isTagPresent ? '' : FILTER_PARAMS.CONNECTING_ROOMS_PARAM_VAL;
              updateURLParams(FILTER_PARAMS.CONNECTING_ROOMS_PARAM, tag);
            }
            // Standard toggle logic for non-bedroom filters
            if (isTagPresent) {
              return {
                ...obj,
                dimensions: existingDimensions?.filter((tag: string) => tag !== filterTag),
                codes: existingDimensions?.filter((tag: string) => tag !== filterTag),
              };
            } else {
              return {
                ...obj,
                dimensions: [...existingDimensions, filterTag],
                codes: [...existingDimensions, getSubstringAfterFirst(filterTag)],
              };
            }
          }
        }
        return obj;
      });
    }

    // Calculate total filter count
    const filterCount = totalFilterCount(payload);

    setFiltersPayload(payload);
    setFiltersCount(filterCount);
    updateSessionObj(payload, filterCount);
    const currentSuites = getCurrentSuites(payload, data?.suites);
    currentSuites?.length !== 0 ? setSuitesData(currentSuites) : setSuitesData(data?.suites);
  };

  const quickFilterSelection = (payload: any[], id: string) => {
    return payload?.some(filter => {
      const existingDimensions = filter?.dimensions || [];
      const isTagPresent = existingDimensions?.includes(id);
      return isTagPresent;
    });
  };

  //Converting the string to Number ...
  const paginationVal = Number(props?.pagination) ?? MOBILE_COL_LENGTH;

  const containerRef = useRef<HTMLDivElement>(null);
  const viewPort = useViewport();
  //logic for pagination
  const totalPages = Math.ceil(suitesData?.length / paginationVal);
  const safeCurrentPage = Math.min(currentPage, totalPages === 0 ? 1 : totalPages);
  const startIndex = (safeCurrentPage - 1) * paginationVal;
  const endIndex = startIndex + paginationVal;
  const previous = props?.previousLabel || 'Previous';
  const next = props?.nextLabel || 'Next';

  const onPageChange = (page: number) => {
    setCurrentPage(page);
    if (containerRef.current) {
      const navbarHeight = viewPort === 'Mobile' ? MOBILE_NAVBAR_HEIGHT : NAVBAR_HEIGHT;
      const containerTop = containerRef.current.getBoundingClientRect().top + window.pageYOffset - navbarHeight;
      window.scrollTo({ top: containerTop, behavior: 'auto' });
    }
  };

  return (
    <StyledLuxurySuites
      data-testid="luxury-LuxurySuites"
      data-component-name="o-luxury-LuxurySuites"
      className={clsx(props?.styleclass)}
    >
      <div className="container" ref={containerRef}>
        <h1 className="py-4 mt-5">{props?.headerText}</h1>
        <div className="all-filters-container mb-5">
          <div className="filter-pill-container d-flex">
            <PillCollection
              onChange={value => handlePillClick(value)}
              customClass={clsx('pill-collection d-flex')}
              pills={pillsData}
            />
            <div className="quick-filter-panel d-flex">
              {props?.quickFilters?.map(
                (
                  filter: {
                    quickFilterId: string;
                    filterTitle: string;
                    filterId: string;
                    clickTrackValue?: string;
                  },
                  index: number
                ) => {
                  /**
                   * in the filter's data checking iff quickFilter is present or not
                   */

                  const isAvailable = filtersData?.some((fD: { data: any[] }) => {
                    return fD?.data?.some((tag: { code: string }) => {
                      return tag?.code === filter?.quickFilterId;
                    });
                  });

                  /**
                   * checking if quickfilter is selected or not...
                   */
                  const isSelected = quickFilterSelection(filtersPayload, filter?.quickFilterId);

                  return (
                    <>
                      {isAvailable && (
                        <>
                          <div className="vertical-divider"></div>

                          <Pill
                            key={index}
                            label={filter?.filterTitle}
                            ariaLabel={filter?.filterTitle}
                            value={filter?.filterTitle}
                            isActive={isSelected}
                            showIcon={isSelected}
                            iconClass={'icon-check'}
                            className="quick-filter-pill pr-2 custom_click_track"
                            onClick={() => onChange(filter?.filterId, filter?.quickFilterId)}
                            custom_click_track_value={filter?.clickTrackValue}
                            id={filter?.quickFilterId}
                          />
                        </>
                      )}
                    </>
                  );
                }
              )}
            </div>
          </div>
        </div>
        <div className="d-flex my-3 pb-3 cards-container">
          {suitesData?.slice(startIndex, endIndex)?.map(
            (
              card: {
                clickTrackValue?: string;
                openInNewTab?: boolean;
                eyebrow?: string;
                title: string;
                logo: string;
                dynamicMedia: renditions;
                ctaLink: string;
              },
              index?: number
            ) => (
              <SuitesCard
                eyebrow={card?.eyebrow}
                key={index}
                title={card?.title}
                logoId={card?.logo}
                dynamicMedia={card.dynamicMedia}
                ctaLink={card?.ctaLink}
                logo={card.logo}
                openInNewTab={card?.openInNewTab}
                isLogoFlagEnabled={isLogoFlagEnabled}
                custom_click_track_value={card?.clickTrackValue}
              />
            )
          )}
        </div>

        {popupOpenState && (
          <div>
            <SuitesFilterPanel
              props={props}
              data={data}
              popupOpenState={popupOpenState}
              setPopupOpenState={setPopupOpenState}
              setFilterButtonClicked={setFilterButtonClicked}
              filterButtonClicked={filterButtonClicked}
              setSuitesData={setSuitesData}
              setFiltersData={setFiltersData}
              setFiltersCount={setFiltersCount}
              filtersData={filtersData}
              filtersPayload={filtersPayload}
              setFiltersPayload={setFiltersPayload}
              isLogoFlagEnabled={isLogoFlagEnabled}
            />
          </div>
        )}

        <Pagination
          currentPage={currentPage}
          totalCount={suitesData?.length ?? 0}
          pageSize={paginationVal}
          labels={{ previous, next }}
          onPageChange={(page: number) => onPageChange(page)}
          isDirectionRightToLeft={direction}
        />
      </div>
    </StyledLuxurySuites>
  );
};

export const LuxurySuitesEditableConfig = {
  emptyLabel: 'LuxurySuitesEditable',
  isEmpty: false,
  resourceType: 'mi-aem-luxury-spa/components/content/suites-catalog/v1/suites-catalog',
};

//Component wrapped with editable component for aem authoring
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const LuxurySuitesEditable = (props: any) => (
  <EditableComponent config={LuxurySuitesEditableConfig} {...props}>
    <LuxurySuites {...props} />
  </EditableComponent>
);
